import type { UsersPermissionsUser } from '@edwin-group/people-graphql';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useLocation, useNavigate } from 'react-router-dom';
import type { SessionResponse } from '@/services/auth/auth.service';

/**
 * Shared "a session was issued, finish signing in" logic. Because MFA is
 * mandatory, the real session is only ever returned by the MFA verify/enable
 * steps — so this post-auth navigation (onboarding vs. home vs. callbackUrl)
 * lives here and is reused by both MFA screens instead of the login screen.
 */
export function useCompleteLogin() {
  const { loginAuth } = useAuthentication();
  const navigate = useNavigate();
  const location = useLocation();
  const callbackUrl = new URLSearchParams(location.search).get('callbackUrl');

  return async function completeLogin(session: SessionResponse) {
    await loginAuth({
      accessToken: session.token || '',
      isAuthenticated: true,
      user: session.user as UsersPermissionsUser,
    });

    const user = session.user;

    if (user?.onboardHomeCompleted) {
      if (callbackUrl && callbackUrl !== '/not-authorized') {
        navigate(callbackUrl);
        return;
      }
      navigate('/');
      return;
    }

    const currentStep = localStorage.getItem(`current-step-${user?.id}`)?.split('?')[0];
    const isValidStep = currentStep && /^\/on-boarding\/step\/\d+$/.test(currentStep);
    navigate(isValidStep ? currentStep : '/on-boarding/step/1');
  };
}
